/**
 * Customer "In-Store Offers" browse hub (iter43).
 *
 * Lists every coupon flagged ``in_store_eligible`` across the tenant.
 * Customer flow:
 *   • Browse — search by shop/code, sort by distance/discount/newest.
 *   • Unlock — burn coins → mint a one-time alphanumeric code.
 *   • Two redemption modes:
 *      - Online: tap "Use online" → routes to cart with code pre-filled.
 *      - Offline: show the unlock code to the shop; merchant types it
 *        into their console to mark redeemed.
 *
 * Coexists with the cart-level coupon picker (which is online-only).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
    ArrowLeft, Search, BadgePercent, MapPin, Coins as CoinsIcon,
    Loader2, Copy, CheckCircle2, X, Store, Ticket, Clock,
} from "lucide-react";

import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const inr2 = (v) => `₹${Number(v || 0).toFixed(0)}`;

/**
 * Friendly relative-time string for an ISO date in the future.
 * Returns something like "2 days left" / "5 hours left" / "Expires in 12 min" /
 * "Expired" / "" (when the date is null/missing).
 */
function expiryLabel(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const ms = t - Date.now();
    if (ms <= 0) return "Expired";
    const hours = ms / 3_600_000;
    const days = Math.floor(hours / 24);
    if (days >= 2) return `${days} days left`;
    if (days === 1) return "1 day left";
    if (hours >= 2) return `${Math.floor(hours)} hours left`;
    const minutes = Math.max(1, Math.floor(ms / 60_000));
    if (hours >= 1) return `${Math.floor(hours)}h ${minutes % 60}m left`;
    return `${minutes} min left`;
}

/** Severity for the expiry chip — drives colour. */
function expirySeverity(iso) {
    if (!iso) return "muted";
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const hours = ms / 3_600_000;
    if (hours < 24) return "urgent";       // < 1 day
    if (hours < 72) return "soon";          // < 3 days
    return "ok";
}

const SORTS = [
    { key: "distance", label: "Nearest" },
    { key: "discount", label: "Best discount" },
    { key: "newest", label: "Newest" },
];

export default function StoreCouponsPage() {
    const nav = useNavigate();
    const { user } = useAuth();

    const [coupons, setCoupons] = useState(null);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("distance");
    const [coinsBalance, setCoinsBalance] = useState(0);
    const [coords, setCoords] = useState({ lat: null, lng: null });
    const [busyId, setBusyId] = useState(null);          // coupon currently being unlocked
    const [unlockedDoc, setUnlockedDoc] = useState(null); // shows the code modal
    const [showHistory, setShowHistory] = useState(false);
    const [historyRows, setHistoryRows] = useState(null);

    // ---- Fetch coords (prefer customer's default address; geo as fallback) ----
    useEffect(() => {
        if (!user) return;
        api.get("/coupons/store/default-coords")
            .then(({ data }) => {
                if (data?.lat != null && data?.lng != null) setCoords({ lat: data.lat, lng: data.lng });
            })
            .catch((err) => console.debug("[StoreCoupons] default-coords skipped:", err));
        api.get("/customer/coins")
            .then(({ data }) => setCoinsBalance(Number(data?.balance) || 0))
            .catch((err) => console.debug("[StoreCoupons] coins fetch skipped:", err));
    }, [user]);

    // ---- Fetch coupon list whenever search / sort / coords change ----
    useEffect(() => {
        const qp = new URLSearchParams();
        if (q) qp.set("q", q);
        if (sort) qp.set("sort", sort);
        if (coords.lat != null && coords.lng != null) {
            qp.set("lat", String(coords.lat));
            qp.set("lng", String(coords.lng));
        }
        api.get(`/coupons/store?${qp.toString()}`)
            .then(({ data }) => setCoupons(Array.isArray(data) ? data : []))
            .catch((err) => {
                console.error("[StoreCoupons] list failed:", err);
                setCoupons([]);
            });
    }, [q, sort, coords]);

    // ---- Actions ----
    const unlock = async (c) => {
        if (!user) { nav("/login", { state: { from: "/store-coupons" } }); return; }
        if (c.unlock_coin_cost > 0 && coinsBalance < c.unlock_coin_cost) {
            toast.error(`Need ${c.unlock_coin_cost} coins. You have ${coinsBalance}.`);
            return;
        }
        setBusyId(c.id);
        try {
            const { data } = await api.post(`/coupons/${c.id}/unlock`);
            setUnlockedDoc(data);
            if (c.unlock_coin_cost > 0) {
                setCoinsBalance((b) => Math.max(0, b - c.unlock_coin_cost));
            }
            toast.success("Coupon unlocked!");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Unlock failed");
        } finally {
            setBusyId(null);
        }
    };

    const applyOnline = (c) => {
        // Navigate to the cart with the code prefilled. CartPage already
        // has the manual coupon input wired to ``apply``.
        nav(`/cart?coupon=${encodeURIComponent(c.code)}`);
    };

    const loadHistory = async () => {
        setShowHistory(true);
        if (historyRows !== null) return;
        try {
            const { data } = await api.get("/coupons/my-unlocks");
            setHistoryRows(data || []);
        } catch (err) {
            console.error("[StoreCoupons] history failed:", err);
            setHistoryRows([]);
        }
    };

    const empty = coupons !== null && coupons.length === 0;

    return (
        <div className="bg-background min-h-screen pb-16">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                <div className="flex items-center gap-2 px-3 py-3">
                    <button onClick={() => nav(-1)} data-testid="store-coupons-back" className="w-9 h-9 rounded-full hover:bg-muted inline-flex items-center justify-center"><ArrowLeft size={18} /></button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-base font-bold leading-none">In-store offers</h1>
                        <p className="text-[11px] text-muted-foreground">Unlock with coins · use online or in-store</p>
                    </div>
                    <button
                        type="button"
                        onClick={loadHistory}
                        data-testid="store-coupons-history-btn"
                        title="Your purchased and redeemed coupons"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1.5 bg-amber-100 text-amber-800 hover:bg-amber-200"
                    >
                        <Ticket size={12} /> My coupons
                    </button>
                </div>
                {/* Search + sort */}
                <div className="px-3 pb-2 flex items-center gap-2">
                    <div className="flex-1 relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            data-testid="store-coupons-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by shop or code"
                            className="w-full pl-7 pr-2 py-2 bg-muted rounded-lg text-xs outline-none"
                        />
                    </div>
                    <select
                        data-testid="store-coupons-sort"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="text-xs font-semibold bg-muted rounded-lg px-2 py-2 outline-none"
                    >
                        {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                </div>
                <div className="px-3 pb-2 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                        <CoinsIcon size={11} className="text-amber-500" />
                        Your coins: <span className="font-bold text-foreground" data-testid="sc-coins-balance">{coinsBalance}</span>
                    </span>
                    {coords.lat != null
                        ? <span className="inline-flex items-center gap-1"><MapPin size={11} /> Sorted near you</span>
                        : <Link to="/profile/addresses" className="text-primary font-semibold underline">Add address for distance</Link>}
                </div>
            </header>

            {/* List */}
            <div className="p-3 space-y-2.5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-3 lg:items-start 2xl:grid-cols-3" data-testid="store-coupons-list">
                {coupons === null && (
                    <div className="text-center py-10 text-muted-foreground inline-flex items-center justify-center gap-2 w-full lg:col-span-full">
                        <Loader2 size={16} className="animate-spin" /> Loading offers…
                    </div>
                )}
                {empty && (
                    <div className="text-center py-12 text-sm text-muted-foreground lg:col-span-full" data-testid="store-coupons-empty">
                        <Ticket size={36} className="mx-auto mb-2 opacity-30" />
                        <p>No in-store offers found.</p>
                        <p className="text-[11px] mt-1">Check back later — merchants add coupons regularly.</p>
                    </div>
                )}
                {(coupons || []).map((c) => (
                    <CouponCard
                        key={c.id}
                        c={c}
                        busy={busyId === c.id}
                        canUnlock={c.unlock_coin_cost === 0 || coinsBalance >= c.unlock_coin_cost}
                        onUnlock={() => unlock(c)}
                        onUseOnline={() => applyOnline(c)}
                    />
                ))}
            </div>

            {/* Unlock-success modal */}
            {unlockedDoc && (
                <UnlockModal doc={unlockedDoc} onClose={() => setUnlockedDoc(null)} />
            )}

            {/* History modal */}
            {showHistory && (
                <HistoryModal rows={historyRows} onClose={() => setShowHistory(false)} />
            )}
        </div>
    );
}

// ---------- Sub-components ----------

function CouponCard({ c, busy, canUnlock, onUnlock, onUseOnline }) {
    const isFlat = (c.discount_type || "").toUpperCase() === "FLAT";
    const isFree = (c.discount_type || "").toUpperCase() === "FREE_DELIVERY";
    const isPercent = !isFlat && !isFree;
    const headline = isFree
        ? "Free delivery"
        : isPercent
            ? `${c.discount_value}% OFF`
            : `Flat ${inr2(c.discount_value)} OFF`;
    const expLabel = expiryLabel(c.valid_till);
    const sev = expirySeverity(c.valid_till);
    // Severity → tailwind classes for the expiry chip.
    const expClass = {
        urgent: "bg-red-100 text-red-700",
        soon: "bg-amber-100 text-amber-700",
        ok: "bg-muted/60 text-muted-foreground",
        expired: "bg-muted text-muted-foreground line-through",
        muted: "bg-muted/60 text-muted-foreground",
    }[sev];

    return (
        <article
            data-testid={`sc-card-${c.code}`}
            className="bg-card border border-border rounded-2xl p-3.5 flex gap-3 active:scale-[0.998] transition"
        >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 text-white inline-flex items-center justify-center font-bold shrink-0 shadow">
                <BadgePercent size={22} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-sm tracking-wider">{c.code}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        {headline}
                    </span>
                    {expLabel && (
                        <span
                            data-testid={`sc-card-expiry-${c.code}`}
                            className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${expClass}`}
                            title={c.valid_till ? new Date(c.valid_till).toLocaleString() : ""}
                        >
                            <Clock size={9} /> {expLabel}
                        </span>
                    )}
                </div>
                <p className="font-bold text-sm leading-tight mt-1 line-clamp-2">{c.title || headline}</p>
                {c.description && (
                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{c.description}</p>
                )}
                <div className="flex items-center gap-1.5 mt-2 text-[11px] text-muted-foreground flex-wrap">
                    {c.shop ? (
                        <span className="inline-flex items-center gap-1 bg-muted/60 rounded-full px-2 py-0.5">
                            <Store size={10} /> {c.shop.name}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 bg-muted/60 rounded-full px-2 py-0.5">All shops</span>
                    )}
                    {c.distance_km != null && (
                        <span className="inline-flex items-center gap-1 bg-muted/60 rounded-full px-2 py-0.5">
                            <MapPin size={10} /> {c.distance_km < 10 ? c.distance_km.toFixed(1) : Math.round(c.distance_km)} km
                        </span>
                    )}
                    {c.min_cart_value > 0 && (
                        <span className="bg-muted/60 rounded-full px-2 py-0.5">Min {inr2(c.min_cart_value)}</span>
                    )}
                    {c.unlock_validity_hours && (
                        <span
                            className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 ring-1 ring-orange-200 rounded-full px-2 py-0.5"
                            title="After unlock, you must redeem within this window"
                        >
                            <Clock size={10} /> Use within {c.unlock_validity_hours}h after unlock
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-2.5">
                    <button
                        type="button"
                        data-testid={`sc-unlock-${c.code}`}
                        onClick={onUnlock}
                        disabled={busy || !canUnlock}
                        className="flex-1 text-xs font-bold rounded-xl py-2 bg-amber-500 text-white disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Ticket size={12} />}
                        {c.unlock_coin_cost > 0
                            ? <>Unlock · <CoinsIcon size={11} className="inline" /> {c.unlock_coin_cost}</>
                            : "Unlock free"}
                    </button>
                    <button
                        type="button"
                        data-testid={`sc-online-${c.code}`}
                        onClick={onUseOnline}
                        className="flex-1 text-xs font-bold rounded-xl py-2 bg-foreground text-background"
                    >Use online</button>
                </div>
            </div>
        </article>
    );
}

function UnlockModal({ doc, onClose }) {
    const [copied, setCopied] = useState(false);
    // Default to QR mode — visiting noisy storefronts is the explicit
    // use case the user asked for. Customers who can't scan still see
    // the code below the QR + the "Code only" tab.
    const [mode, setMode] = useState("qr"); // qr | code

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(doc.unlock_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) { console.debug("[UnlockModal] clipboard:", err); }
    };

    // QR payload includes a ``bizil://`` URI scheme + the code so a
    // merchant scanning with their default camera app gets a tappable
    // link that can pre-fill the redeem form once we ship a custom
    // scanner. For now the payload doubles as plain text so any QR
    // scanner just extracts the 8-char code into the clipboard.
    const qrPayload = `bizil-unlock:${doc.unlock_code}`;

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="sc-unlock-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-card w-full max-w-md md:max-w-lg rounded-3xl p-5 shadow-2xl">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                            <CheckCircle2 size={11} /> Unlocked
                        </div>
                        <h3 className="font-bold text-lg mt-1">{doc.coupon_code}</h3>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground" data-testid="sc-unlock-modal-close"><X size={20} /></button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Show this to the shop to claim your offer in person.</p>

                {/* QR / Code tabs */}
                <div className="mt-3 inline-flex rounded-full bg-muted p-0.5 text-[11px] font-semibold w-full">
                    <button
                        type="button"
                        data-testid="sc-unlock-tab-qr"
                        onClick={() => setMode("qr")}
                        className={`flex-1 rounded-full py-1.5 transition ${mode === "qr" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}
                    >Scan QR</button>
                    <button
                        type="button"
                        data-testid="sc-unlock-tab-code"
                        onClick={() => setMode("code")}
                        className={`flex-1 rounded-full py-1.5 transition ${mode === "code" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}
                    >Code only</button>
                </div>

                {mode === "qr" ? (
                    <div className="my-4 bg-white border-2 border-dashed border-amber-400 rounded-2xl p-4 text-center">
                        <div className="flex items-center justify-center" data-testid="sc-unlock-qr">
                            <QRCodeSVG
                                value={qrPayload}
                                size={196}
                                level="M"
                                marginSize={2}
                                bgColor="#FFFFFF"
                                fgColor="#0B0B0B"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-3">
                            Ask the shop to scan with their phone camera.
                        </p>
                        <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                            <span className="font-mono text-base font-bold tracking-[0.25em] text-amber-900" data-testid="sc-unlock-code-mini">{doc.unlock_code}</span>
                            <button
                                type="button"
                                onClick={copy}
                                className="text-amber-700"
                                data-testid="sc-unlock-copy-mini"
                                title="Copy code"
                            >{copied ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Copy size={14} />}</button>
                        </div>
                    </div>
                ) : (
                    <div className="my-4 bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 border-2 border-dashed border-amber-400 rounded-2xl p-5 text-center">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">Your unlock code</p>
                        <p className="font-mono text-3xl font-extrabold tracking-[0.3em] text-amber-900 mt-1.5" data-testid="sc-unlock-code">
                            {doc.unlock_code}
                        </p>
                        <button
                            type="button"
                            onClick={copy}
                            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-white border border-amber-300 rounded-lg px-3 py-1.5"
                            data-testid="sc-unlock-copy"
                        >
                            {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} />}
                            {copied ? "Copied" : "Copy code"}
                        </button>
                    </div>
                )}

                <div className="text-[11px] text-muted-foreground space-y-1.5">
                    {doc.coins_spent > 0 && <p>· {doc.coins_spent} coins were deducted.</p>}
                    {doc.valid_till && (
                        <p>
                            ·{" "}
                            <span className={
                                expirySeverity(doc.valid_till) === "urgent" ? "font-bold text-red-700"
                                    : expirySeverity(doc.valid_till) === "soon" ? "font-bold text-amber-700"
                                        : ""
                            }>
                                {expiryLabel(doc.valid_till)} ·{" "}
                            </span>
                            valid till {new Date(doc.valid_till).toLocaleString()}.
                        </p>
                    )}
                    <p>· One-time use. Code becomes inactive once redeemed.</p>
                    <p>· To use online: apply <span className="font-mono font-bold">{doc.coupon_code}</span> at checkout.</p>
                </div>
            </div>
        </div>
    );
}

function HistoryModal({ rows, onClose }) {
    // iter43g: rename + segment by status, and surface the post-unlock
    // deadline countdown so the customer can see "use within X hours"
    // at a glance. Three tabs: Active (UNLOCKED + not expired),
    // Redeemed, Expired.
    const [tab, setTab] = useState("active");

    const buckets = useMemo(() => {
        const a = { active: [], redeemed: [], expired: [] };
        for (const r of rows || []) {
            const status = String(r?.status || "").toUpperCase();
            if (status === "REDEEMED") a.redeemed.push(r);
            else if (status === "EXPIRED" || status === "VOID") a.expired.push(r);
            else a.active.push(r);
        }
        return a;
    }, [rows]);

    const TABS = [
        { key: "active", label: "Active", count: buckets.active.length },
        { key: "redeemed", label: "Redeemed", count: buckets.redeemed.length },
        { key: "expired", label: "Expired", count: buckets.expired.length },
    ];
    const list = buckets[tab] || [];

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="sc-history-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-card w-full max-w-md md:max-w-lg rounded-3xl p-5 max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-lg inline-flex items-center gap-2">
                        <Ticket size={18} className="text-amber-500" /> My coupons
                    </h3>
                    <button onClick={onClose} className="text-sm font-semibold text-muted-foreground" data-testid="sc-history-close">Close</button>
                </div>

                {/* Segment tabs */}
                <div className="flex items-center gap-1 bg-muted rounded-full p-0.5 text-[11px] font-semibold mb-3">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            data-testid={`sc-history-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`flex-1 rounded-full py-1.5 transition ${tab === t.key ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}
                        >
                            {t.label} <span className="opacity-70">({t.count})</span>
                        </button>
                    ))}
                </div>

                {rows === null && (
                    <div className="text-center py-6 text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                )}
                {rows && list.length === 0 && (
                    <div className="text-center py-10 text-sm text-muted-foreground">
                        {tab === "active" && (
                            <>
                                <Ticket size={28} className="mx-auto mb-2 opacity-30" />
                                <p>No active coupons.</p>
                                <p className="text-[11px] mt-1">Unlock one from the store offers list to use it at a shop.</p>
                            </>
                        )}
                        {tab === "redeemed" && "Nothing redeemed yet."}
                        {tab === "expired" && "No expired coupons."}
                    </div>
                )}
                {rows && list.length > 0 && (
                    <div className="space-y-2">
                        {list.map((r) => (
                            <MyCouponRow key={r.id} r={r} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function MyCouponRow({ r }) {
    const expLabel = expiryLabel(r.valid_till);
    const sev = expirySeverity(r.valid_till);
    const expClass = {
        urgent: "bg-red-100 text-red-700",
        soon: "bg-amber-100 text-amber-700",
        ok: "bg-muted/60 text-muted-foreground",
        expired: "bg-muted text-muted-foreground line-through",
        muted: "bg-muted/60 text-muted-foreground",
    }[sev];
    const status = String(r.status || "UNLOCKED").toUpperCase();
    const isActive = status === "UNLOCKED";
    const headline = (r.discount_type || "").toUpperCase() === "PERCENT"
        ? `${r.discount_value}% off`
        : (r.discount_type || "").toUpperCase() === "FLAT"
            ? `${inr2(r.discount_value)} off`
            : "Free delivery";

    return (
        <div data-testid={`sc-history-${r.unlock_code}`} className="bg-muted/40 rounded-xl p-3">
            <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold text-sm">{r.coupon_code}</span>
                <StatusBadge status={status} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{headline}</p>
            <div className="flex items-center justify-between mt-1.5">
                <span className="font-mono text-xs tracking-widest text-foreground/80">{r.unlock_code}</span>
                {r.coins_spent > 0 && <span className="text-[10px] text-muted-foreground">−{r.coins_spent} coins</span>}
            </div>
            {isActive && expLabel && (
                <div className={`mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${expClass}`}>
                    <Clock size={9} /> {expLabel}
                </div>
            )}
            {status === "REDEEMED" && r.redeemed_at && (
                <p className="mt-1 text-[10px] text-emerald-700">Redeemed {new Date(r.redeemed_at).toLocaleDateString()}</p>
            )}
        </div>
    );
}

const STATUS_STYLES = {
    UNLOCKED: "bg-amber-100 text-amber-700",
    REDEEMED: "bg-emerald-100 text-emerald-700",
    EXPIRED: "bg-muted text-muted-foreground",
    VOID: "bg-red-100 text-red-700",
};
function StatusBadge({ status }) {
    const cls = STATUS_STYLES[status] || "bg-muted text-muted-foreground";
    return <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{status || "—"}</span>;
}
